import React from 'react';
import classNames from 'classnames';
import s from './AiAnalyzingBorder.scss';
const RADIUS = 16;
const STROKE = 1.5;
const BLEED = 1;
const INSET = STROKE / 2 + BLEED;
const SEGMENT = 20;
const CARD_PHASE_OFFSETS = [0, 0.85, 1.55, 2.2, 2.95, 3.6];
const COMET_STOPS = [{
  offset: '0%',
  color: '#caebff'
}, {
  offset: '45%',
  color: '#7b91ff'
}, {
  offset: '100%',
  color: '#5532da'
}];
const ORBIT_DURATION_S = 2;
const ORBIT_CARD_LEAD_FRACTION_OF_LOOP = 0.08;
const getOrbitDelay = (cardIndex, cardCount) => -Math.max(0, cardCount - 1 - cardIndex) * ORBIT_DURATION_S * ORBIT_CARD_LEAD_FRACTION_OF_LOOP;
export const AiAnalyzingBorder = _ref => {
  let {
    mode,
    cardIndex,
    variant = 'comet',
    cardCount = 1
  } = _ref;
  const rootRef = React.useRef(null);
  const travelRef = React.useRef(null);
  const offsetRef = React.useRef('0');
  const [size, setSize] = React.useState({
    width: 0,
    height: 0
  });
  const isOrbit = variant === 'orbit';
  const gradientId = React.useMemo(() => `ai-analyzing-comet-${cardIndex}`, [cardIndex]);
  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (isOrbit || !el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      var _entries$;
      const rect = (_entries$ = entries[0]) == null ? void 0 : _entries$.contentRect;
      if (rect) {
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOrbit]);
  React.useEffect(() => {
    if (isOrbit || mode !== 'travel' || typeof requestAnimationFrame === 'undefined') {
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      const rect = travelRef.current;
      if (rect) {
        offsetRef.current = getComputedStyle(rect).strokeDashoffset;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, isOrbit, size.width, size.height]);
  React.useLayoutEffect(() => {
    const rect = travelRef.current;
    if (!rect) {
      return;
    }
    rect.style.strokeDashoffset = mode === 'grow' ? offsetRef.current : '';
  }, [mode, size.width, size.height]);
  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!isOrbit || !el) {
      return;
    }
    el.style.setProperty('--orbit-duration', `${ORBIT_DURATION_S}s`);
    el.style.setProperty('--orbit-delay', `${getOrbitDelay(cardIndex, cardCount)}s`);
  }, [isOrbit, cardIndex, cardCount]);
  const {
    width,
    height
  } = size;
  const cometDelay = -CARD_PHASE_OFFSETS[cardIndex % CARD_PHASE_OFFSETS.length];
  return /*#__PURE__*/React.createElement("div", {
    ref: rootRef,
    className: classNames(s.root, {
      [s.orbit]: isOrbit,
      [s.modeTravel]: mode === 'travel',
      [s.modeGrow]: mode === 'grow',
      [s.modeFade]: mode === 'fade'
    }),
    "data-hook": "ai-analyzing-border",
    "aria-hidden": true
  }, isOrbit ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: s.orbitRing
  }), /*#__PURE__*/React.createElement("div", {
    className: s.orbitFill,
    "data-hook": "ai-analyzing-orbit-fill"
  })) : width > 0 && height > 0 && /*#__PURE__*/React.createElement("svg", {
    className: s.svg,
    width: width,
    height: height,
    viewBox: `0 0 ${width} ${height}`,
    preserveAspectRatio: "none",
    fill: "none"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    id: gradientId,
    x1: "0",
    y1: "0",
    x2: "1",
    y2: "1"
  }, COMET_STOPS.map(stop => /*#__PURE__*/React.createElement("stop", {
    key: stop.offset,
    offset: stop.offset,
    stopColor: stop.color
  })))), /*#__PURE__*/React.createElement("rect", {
    ref: travelRef,
    className: s.travel,
    x: INSET,
    y: INSET,
    width: width - INSET * 2,
    height: height - INSET * 2,
    rx: RADIUS,
    pathLength: 100,
    stroke: `url(#${gradientId})`,
    strokeDasharray: `${SEGMENT} ${100 - SEGMENT}`,
    style: {
      animationDelay: `${cometDelay}s`
    }
  })));
};
//# sourceMappingURL=AiAnalyzingBorder.js.map